/**
 * Browser glue for game backups (the pure logic — what a file contains, how it
 * is validated — is `game/backup.ts`).
 *
 * Saving hands the file to the phone's share sheet where it can carry a .json
 * file (WhatsApp, Drive, email — anywhere off the device), and otherwise falls
 * back to an ordinary download. Restoring reads a file the person picked.
 */

import { backupFileName, buildBackup, describeImport, parseBackup, planImport } from '../game/backup';
import type { GameStore } from '../game/store';
import type { Game } from '../game/types';
import { saveFile } from './resultsShare';

/** A game with many photos can be large; refuse anything that would risk freezing the browser while it is read. */
const MAX_BACKUP_BYTES = 150 * 1024 * 1024;

export type SaveOutcome = { kind: 'shared' | 'saved'; fileName: string } | { kind: 'cancelled' };

function canShareJson(file: File): boolean {
  if (typeof navigator === 'undefined') return false;
  if (typeof navigator.share !== 'function' || typeof navigator.canShare !== 'function') return false;
  try {
    return navigator.canShare({ files: [file] });
  } catch {
    return false;
  }
}

/** Saves `games` as one file the person can keep somewhere safe. */
export async function saveBackupFile(games: Game[], now: number = Date.now()): Promise<SaveOutcome> {
  const fileName = backupFileName(games, now);
  const file = new File([buildBackup(games, now)], fileName, { type: 'application/json' });
  if (canShareJson(file)) {
    try {
      await navigator.share({ files: [file], title: fileName.replace(/\.json$/i, '') });
      return { kind: 'shared', fileName };
    } catch (err) {
      // Closing the share sheet without choosing anything is not an error.
      if (err instanceof DOMException && err.name === 'AbortError') return { kind: 'cancelled' };
      // Any other refusal (a browser that lists JSON as shareable but rejects it): just download it.
    }
  }
  saveFile(file);
  return { kind: 'saved', fileName };
}

/** The line shown after saving. */
export function describeSave(outcome: SaveOutcome): string {
  if (outcome.kind === 'cancelled') return 'Nothing was saved.';
  if (outcome.kind === 'shared') return `Sent ${outcome.fileName}. Keep it somewhere safe — Restore Games brings it back.`;
  return `Saved ${outcome.fileName} to this device's downloads. Keep a copy somewhere safe — Restore Games brings it back.`;
}

/** Reads a backup the person picked and saves the games it holds. Returns the one-line result to show. */
export async function restoreBackupFile(file: File, store: GameStore): Promise<{ ok: boolean; message: string }> {
  if (file.size > MAX_BACKUP_BYTES) return { ok: false, message: 'That file is too large to be a game file.' };
  let text: string;
  try {
    text = await file.text();
  } catch {
    return { ok: false, message: "That file couldn't be read." };
  }
  const parsed = parseBackup(text);
  if (!parsed.ok) return { ok: false, message: parsed.error };

  const existing = new Set((await store.listAll()).map((g) => g.id));
  const plan = planImport(existing, parsed.games);
  try {
    for (const game of plan.toSave) await store.save(game);
  } catch {
    return { ok: false, message: "Some games couldn't be saved — this device may be out of storage. Nothing already here was changed." };
  }
  return { ok: plan.toSave.length > 0, message: describeImport(plan.toSave.length, plan.alreadyHave, parsed.rejected.length) };
}
